/**
 * The words on a person's card (components/PersonCard.svelte), the same card on main People and
 * a group's People. The numbers come from packages/core (personOverview); this only says them,
 * from whoever is looking: you (positive = they owe you), the person themself, or a member looking
 * at someone else in a group (balances are between them and the treasurer).
 */
import {
  formatCents,
  formatMicro,
  hoursText,
  type PersonOverview,
  type PersonSource,
  type SourceRowKey,
  type StatementEntry,
} from "@split/core";
import { fill, shows } from "../settings/index.ts";
import * as T from "../settings/people.ts";
import { shortDate, shortDateTime } from "./format.ts";

/** Main People (every source), one group, or Tutoring (that person's tutoring only). */
export type CardScope = "everyone" | "group" | "tutoring";

export interface CardViewer {
  /** owner: you (Miles or a treasurer); self: their own card; member: someone else's, as a member. */
  side: "owner" | "self" | "member";
  /** Who runs the group, for a member's words. */
  treasurer?: string;
}

/** A group's one-line summary of one person: when it's overdue, or paid up. The amount is beside it. */
export function groupSentence(s: PersonSource, v: CardViewer): string {
  if (v.side !== "owner") {
    if (s.status === "you") return fill(T.CARD_M_TREASURER_TEXT, { treasurer: v.treasurer ?? "" });
    if (s.status === "owes" || s.status === "owed") return T.CARD_M_OWES;
    return s.totalMicro ? T.CARD_M_PAID_UP : T.CARD_M_NONE;
  }
  switch (s.status) {
    case "you":
      return fill(T.CARD_YOU_TEXT, { amount: formatMicro(s.totalMicro ?? 0) });
    case "owes":
      return fill(T.CARD_OWES_TEXT, {
        since: s.oldestUnpaid
          ? fill(T.CARD_OLDEST_TEXT, {
              date: shortDate(s.oldestUnpaid),
              days: s.daysOverdue ?? 0,
              dayword: s.daysOverdue === 1 ? T.CARD_DAY : T.CARD_DAYS,
            })
          : "",
      });
    case "owed":
      return T.CARD_OWED_TEXT;
    case "paid":
      return fill(T.CARD_PAID_TEXT, {
        last: s.lastPaid ? fill(T.CARD_LAST_PAID, { when: shortDateTime(s.lastPaid) }) : "",
      });
    default:
      return T.CARD_NONE_TEXT;
  }
}

/**
 * A balance as a plain figure, bank-style from the person's side (Miles, 2026-10-23): what they
 * owe is minus and red, a credit (tutoring pay, you owe them) is plus and green, square is $0.00.
 * The same for every viewer, so the sign never flips between screens.
 */
export const figure = (cents: number) => signed(cents);
/** The color of a figure: "owes" (red), "credit" (green) or "" (square). */
export const tone = (cents: number): "owes" | "credit" | "" =>
  cents > 0 ? "owes" : cents < 0 ? "credit" : "";

/** One source for the summary: "I can't fly −$110.63". */
export function sourceShort(s: PersonSource): string {
  return fill(T.SUMMARY_PART, { label: s.label, amount: figure(s.balanceCents) });
}

/**
 * The card's summary line. Inside a group: that group's sentence. On main People: each source
 * with something in it, or "Paid up everywhere".
 */
export function summaryText(o: PersonOverview, scope: CardScope, v: CardViewer): string {
  if (scope === "group" && o.sources[0]) return groupSentence(o.sources[0], v);
  // In Tutoring there's one source, and its figure is already the card's amount.
  if (scope === "tutoring") return "";
  // A family's lessons are one part however many of their students there are.
  const families = o.sources.filter((s) => s.role === "student");
  const family = families.reduce((t, s) => t + s.balanceCents, 0);
  const parts = [
    ...o.sources
      .filter((s) => s.role !== "student")
      .filter(
        (s) =>
          s.balanceCents !== 0 ||
          (s.role === "tutor" && s.rows.some((r) => r.key === "earned" && r.cents)),
      )
      .map((s) => sourceShort(s)),
    ...(families.length && family !== 0
      ? [fill(T.SUMMARY_PART, { label: T.TOTAL_TUTORING, amount: figure(family) })]
      : []),
  ];
  return parts.length ? parts.join(T.SUMMARY_JOIN) : T.PEOPLE_PAID_UP_EVERYWHERE;
}

/** Two or more sources with something in them: the total is worth spelling out. */
export const netted = (o: PersonOverview) =>
  o.sources.filter((s) => s.balanceCents !== 0).length > 1;

/** "Balance left −$10.63" on main People when it adds up more than one source, else "". */
export function totalText(o: PersonOverview, scope: CardScope): string {
  return scope === "everyone" && netted(o) && shows(T.PEOPLE_TOTAL_TEXT)
    ? fill(T.PEOPLE_TOTAL_TEXT, { amount: figure(o.totalCents) })
    : "";
}

export interface TotalRow {
  key: "spent" | "groups" | "tutoring" | "left";
  label: string;
  value: string;
  /** Its color: "owes", "credit" or "" (a plain amount, like Total spent). */
  tone: "owes" | "credit" | "";
  /** Not part of this view (inside a group there's no tutoring; in Tutoring no groups). */
  hidden: boolean;
}

/**
 * The four totals, always in this order: Total spent, All groups, Tutoring, Balance left. Each
 * view shows the ones that apply: main People all of them (that the person has), a group its
 * items and balance, Tutoring that person's tutoring and balance.
 */
export function totalRows(o: PersonOverview, scope: CardScope): TotalRow[] {
  const groups = o.sources.some((s) => s.category === "purchases");
  const tutoring = o.sources.some((s) => s.category === "tutoring");
  return [
    {
      key: "spent",
      label: T.TOTAL_SPENT,
      value: formatCents(o.spentCents),
      tone: "",
      hidden: !groups || scope === "tutoring",
    },
    {
      key: "groups",
      label: T.TOTAL_GROUPS,
      value: figure(o.purchasesCents),
      tone: tone(o.purchasesCents),
      hidden: !groups || scope !== "everyone",
    },
    {
      key: "tutoring",
      label: T.TOTAL_TUTORING,
      value: figure(o.tutoringCents),
      tone: tone(o.tutoringCents),
      hidden: !tutoring || scope === "group",
    },
    {
      key: "left",
      label: T.TOTAL_LEFT,
      value: figure(o.totalCents),
      tone: tone(o.totalCents),
      hidden: false,
    },
  ];
}

export interface ShownRow {
  key: SourceRowKey | "balance";
  label: string;
  amount: string;
  /** Only the balance row is colored. */
  tone: "owes" | "credit" | "";
}

const ROW_LABEL: Record<SourceRowKey, (v: CardViewer) => string> = {
  items: (v) => (v.side === "self" ? T.ROW_YOUR_ITEMS : T.ROW_ITEMS),
  fronted: () => T.ROW_FRONTED,
  paid: (v) =>
    v.treasurer && v.side !== "owner"
      ? fill(T.ROW_PAID_TREASURER, { treasurer: v.treasurer })
      : v.side === "self"
        ? T.ROW_FAMILY_PAID_SELF
        : T.ROW_PAID,
  earned: (v) => (v.side === "self" ? T.ROW_EARNED_SELF : T.ROW_EARNED),
  "paid-out": (v) => (v.side === "self" ? T.ROW_PAID_OUT_SELF : T.ROW_PAID_OUT),
  bought: (v) => (v.side === "self" ? T.ROW_BOUGHT_SELF : T.ROW_BOUGHT),
  charged: () => T.ROW_CHARGED,
  "family-paid": (v) => (v.side === "self" ? T.ROW_FAMILY_PAID_SELF : T.ROW_FAMILY_PAID),
};
/** Rows that always show; the rest only when there's something in them. */
const ALWAYS = new Set<SourceRowKey>(["items", "paid", "earned", "charged", "family-paid"]);

/**
 * A source's rows as shown, then its balance: the amounts (without signs, the labels say which
 * way) add up to the balance exactly. A tutor's "still in a group" row is the old System payment
 * counted in the group instead (see packages/core tutorSummary).
 */
export function rowsOf(s: PersonSource, v: CardViewer): ShownRow[] {
  const rows: ShownRow[] = s.rows
    .filter((r) => ALWAYS.has(r.key) || r.cents !== 0)
    .map((r) => ({
      key: r.key,
      label: ROW_LABEL[r.key](v),
      amount: formatCents(Math.abs(r.cents)),
      tone: "" as const,
    }));
  if (!s.you)
    rows.push({
      key: "balance",
      label: T.ROW_BALANCE,
      amount: figure(s.balanceCents),
      tone: tone(s.balanceCents),
    });
  return rows;
}

const METHOD: Record<string, string> = {
  zelle: T.METHOD_ZELLE,
  cash: T.METHOD_CASH,
  card: T.METHOD_CARD,
};
const method = (m: string | undefined) => METHOD[m ?? ""] ?? T.METHOD_OTHER;
const note = (n: string | undefined) => (n ? fill(T.STMT_NOTE, { note: n }) : "");

/** When a statement line happened: "Oct 14" or "Oct 14, 16:00". */
export const entryDate = (e: StatementEntry) =>
  e.on.length > 10 ? shortDateTime(e.on) : shortDate(e.on);

/** A statement line with where it's from: "Tutoring pay · 1 hour lesson, 16:00–17:00 · Yulin Wang". */
export function entryLine(e: StatementEntry, source: string, v: CardViewer): string {
  return fill(T.STMT_LINE, { source, what: entryText(e, v) });
}

/** What a statement line was, in words. */
export function entryText(e: StatementEntry, v: CardViewer): string {
  const self = v.side === "self";
  switch (e.kind) {
    case "item":
      return fill(T.STMT_ITEM, { name: e.name ?? "" });
    case "fronted":
      return fill(T.STMT_FRONTED, { name: e.name ?? "" });
    case "payment":
      // A payment from them takes off what they owe (minus); one you made them adds to it.
      return fill(
        e.cents <= 0
          ? self
            ? T.STMT_PAYMENT_SELF
            : T.STMT_PAYMENT
          : self
            ? T.STMT_PAYMENT_OUT_SELF
            : T.STMT_PAYMENT_OUT,
        { note: note(e.note) },
      );
    case "lesson":
      return fill(T.STMT_LESSON, {
        hours: hoursText(e.minutes ?? 0),
        time: e.start && e.end ? fill(T.STMT_LESSON_TIME, { start: e.start, end: e.end }) : "",
        student: e.student ? fill(T.STMT_LESSON_STUDENT, { student: e.student }) : "",
      });
    case "payout":
      return fill(self ? T.STMT_PAYOUT_SELF : T.STMT_PAYOUT, { method: method(e.method) });
    case "bought":
      return fill(T.STMT_BOUGHT, { name: e.name ?? "" });
    case "purchase":
      return fill(T.STMT_PURCHASE, { hours: hoursText(e.minutes ?? 0) });
    case "family-payment":
      return fill(self ? T.STMT_FAMILY_PAYMENT_SELF : T.STMT_FAMILY_PAYMENT, {
        note: note(method(e.method)),
      });
    default:
      return fill(T.STMT_CHARGE, { name: e.name ?? "" });
  }
}

/**
 * A statement amount from the person's side, like their bank account: a purchase is minus, a
 * payment or tutoring pay is plus ("+$20.00 tutoring, Oct 14 lesson"). `c` is the core's sign
 * (plus adds to what they owe you), so it's turned around here.
 */
export const signed = (c: number) =>
  c < 0 ? `+${formatCents(-c)}` : c > 0 ? `−${formatCents(c)}` : formatCents(0);

/** Their color on main People: the one they have in their first group, if any. */
export const overallColor = (o: PersonOverview) =>
  o.sources.find((s) => s.category === "purchases" && s.color)?.color ?? null;

const LINK_WORDS: Record<string, string> = {
  you: T.TRACE_LINK_YOU,
  account: T.TRACE_LINK_ACCOUNT,
  identity: T.TRACE_LINK_IDENTITY,
  tutor: T.TRACE_LINK_TUTOR,
};
const linkText = (ls: readonly { kind: string; detail: string }[]) =>
  ls.map((l) => fill(LINK_WORDS[l.kind] ?? "{detail}", { detail: l.detail })).join(T.TRACE_JOIN);

export interface TraceLine {
  /** "Parents Run · Miles Nguyen" */
  line: string;
  /** "joined by account Jianing Dong", or "first line". */
  why: string;
  /** "Left out: same person (Tia Jiang) …", or "". */
  left: string;
}

/**
 * Why each line is on a main People row (owner only): the links the server says joined it, and
 * any link it left out. For checking a card that looks wrong (bug 2026-10-28: Miles's own
 * Parents Run receipt showed on Jianing Dong's card).
 */
export function joinTrace(
  lines: readonly {
    label: string;
    recordName?: string;
    joinedBy?: { kind: string; detail: string }[];
    ignoredLinks?: { kind: string; detail: string }[];
  }[],
): TraceLine[] {
  return lines.map((l) => ({
    line: fill(T.TRACE_LINE, { label: l.label, name: l.recordName ?? "" }),
    why: l.joinedBy?.length ? fill(T.TRACE_BY, { links: linkText(l.joinedBy) }) : T.TRACE_FIRST,
    left: l.ignoredLinks?.length ? fill(T.TRACE_LEFT_OUT, { links: linkText(l.ignoredLinks) }) : "",
  }));
}
